import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import Spacer from '../../components/Spacer';
import Link from '../../components/Link';
import { Formik, FormikProps } from 'formik';
import Form from '../../components/form/Form';
import FormButton from '../../components/form/FormButton';
import FormInput from '../../components/form/FormInput';
import * as Yup from 'yup';
import { useUserContext } from '../../context/UserContext';
import { useDeleteUser } from '../../hooks/user/useDeleteUser';
import { useLogout } from '../../hooks/user/useLogout';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Platform } from 'react-native';
import WebViewModal from '../modals/WebViewModal';
import accessibilityTags from '../../../accessibilityTags';
import AlertDialog from '../../components/AlertDialog';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const StyledScrollView = styled(KeyboardAwareScrollView)`
  background-color: #fdfbf7;
  padding: 32px 23px 32px 23px;
  flex: 1;
`;

const StyledContainer = styled.View`
  flex: 1;
  width: 100%;
  align-self: center;
  max-width: 768px;
  margin-top: ${Platform.OS === 'web' ? 0 : 10}px;
`;
const StyledSubtitle = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 16px;
  line-height: 18px;
  color: #141027;
`;

const StyledButtonContainer = styled.View`
  position: absolute;
  bottom: 0;
  left: -10px;
  right: -10px;
`;

const StyledLink = styled(Link)`
  font-family: 'DMSans-Medium';
  font-size: 16px;
  line-height: 20px;
  color: blue;
`;

const MyProfileDeleteDataScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const formikRef = React.useRef<FormikProps<any> | null>();
  const mountRef = useRef(false);
  const { user } = useUserContext();
  const [openUrl, setOpenUrl] = useState<{ title: string; url: string }>();
  const { deletedUser, deleteUserError, deleteUser } = useDeleteUser(user?.id);
  const { logout, isLoggingOut } = useLogout();
  const [openAlert, setOpenAlert] = useState<boolean>();

  useEffect(() => {
    if (deletedUser) {
      logout();
    }
  }, [deletedUser, logout]);

  useEffect(() => {
    if (deleteUserError?.statusCode === 409) {
      setOpenAlert(true);
      formikRef.current?.setSubmitting(false);
    } else if (deleteUserError) {
      formikRef.current?.setFieldError('currentPassword', 'Incorrect password');
      formikRef.current?.setSubmitting(false);
    }
  }, [deleteUserError]);

  useEffect(() => {
    if (!mountRef.current && isLoggingOut) {
      navigation.replace('IntroNavigator', {
        screen: 'LoginScreen'
      });
    }
    mountRef.current = true;
    return () => {
      mountRef.current = false;
    };
  }, [isLoggingOut, navigation]);
  return (
    <StyledScrollView contentContainerStyle={{ flexGrow: 1 }}>
      <StyledContainer>
        <StyledSubtitle>
          Deleting your data will also delete your Chloe account. Any previously
          shared data will remain anonymously shared. Review our{' '}
          <StyledLink
            onPress={() => {
              const url = 'https://peoplescience.health/privacy-policy/';
              if (Platform.OS == 'web') {
                window.open(url, '_blank');
              } else {
                setOpenUrl({ title: 'Privacy Policy', url });
              }
            }}
          >
            Privacy Policy
          </StyledLink>{' '}
          here.
        </StyledSubtitle>
        <Spacer size={5} />
        <Formik
          innerRef={formik => (formikRef.current = formik)}
          validateOnMount={true}
          validationSchema={Yup.object({
            currentPassword: Yup.string().required(
              'Current password is required'
            )
          })}
          onSubmit={formValues => {
            if (user) {
              deleteUser({ password: formValues['currentPassword'] });
            }
          }}
          initialValues={{ currentPassword: '' }}
        >
          {
            <Form>
              <FormInput
                accessibilityLabel={accessibilityTags.deleteAccountPasswordID}
                testID={accessibilityTags.deleteAccountPasswordID}
                secureTextEntry
                returnKeyType='done'
                name='currentPassword'
                placeholder='Current Password'
              />
              <StyledButtonContainer>
                <FormButton
                  accessibilityLabel={accessibilityTags.deleteAccountButtonID}
                  testID={accessibilityTags.deleteAccountButtonID}
                  color='secondary'
                >
                  Delete My Account
                </FormButton>
              </StyledButtonContainer>
            </Form>
          }
        </Formik>
      </StyledContainer>
      {!!openUrl && (
        <WebViewModal
          visible
          title={openUrl.title}
          url={openUrl.url}
          onClose={() => setOpenUrl(undefined)}
        />
      )}
      {openAlert && (
        <AlertDialog
          title='Unable to delete the account'
          message='If you would like to delete your account, please contact your study team to withdraw from your study. Once withdrawn you will be able to delete your account.'
          onCloseClicked={() => setOpenAlert(false)}
        />
      )}
    </StyledScrollView>
  );
};

export default MyProfileDeleteDataScreen;
